import React, { useContext } from 'react';
import i18next from 'core/i18n';
import { Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  Tooltip,
  palettes,
  TinyBody,
  ColumnDefinition,
  Table,
  RowDefinition,
  PageInfo,
  RegularTitle,
  TagsFooterProps
} from '@indigo-ag/component-module';
import { faExclamationCircle, faFileAlt, faDrawPolygon } from '@fortawesome/pro-regular-svg-icons';
import { faStar, faBell, faCheckCircle, faMinusCircle } from '@fortawesome/pro-solid-svg-icons';
import { CollateralType } from '@indigo-ag/schema';

import {
  CURRENCY_SYMBOL,
  CopyToClipboardButton,
  NoResultsFound,
  Permissions,
  TABLE_ACTIONS,
  TableSort,
  createStylesHook,
  AgFinanceQueueCustomActions
} from 'shared';
import { brazil, currency, date, text } from 'shared/utils';
import { FormattedAgFinanceRecord, calculateStages } from '../shared-br-grower';
import { PillIds, TabIds } from '../pages/tabs/config';
import { UserContext } from 'core/components/GateWay/GateWay';
import { EditableCreditAnalyst } from '../../../shared/components/EditableCreditAnalyst/EditableCreditAnalyst';

interface AgFinancingQueueProps {
  queue: FormattedAgFinanceRecord[];
  pagination?: PageInfo;
  onTableAction?: (action: any) => void;
  activeTab: TabIds | null;
  sort: TableSort | null;
  searching: boolean;
  onDetailClick?: () => void;
  activePill: PillIds | null;
}

interface IDefinitionContext {
  showCopyButton: boolean;
  onDetailClick?: AgFinancingQueueProps['onDetailClick'];
  isTerramagna: boolean;
  displayStage: boolean;
  activeTab: AgFinancingQueueProps['activeTab'];
  isCreditLead: boolean;
  isCredit: boolean;
  isSuper: boolean;
}

const useStyles = createStylesHook({
  tableStyles: {
    '& div[role="table"]': {
      color: palettes.brand.gray['800']
    }
  }
});

const invalidCpfCnpjTooltip = (
  <Tooltip
    color={palettes.legacy.orange.default}
    style={{ marginLeft: 8 }}
    title={<TinyBody>{i18next.t('cpfInvalid')}</TinyBody>}
  >
    <FontAwesomeIcon icon={faExclamationCircle} color={palettes.legacy.orange.default} />
  </Tooltip>
);

export type ColumnId =
  | 'name'
  | 'campaign'
  | 'stage'
  | 'state'
  | 'cpfOrCnpj'
  | 'date'
  | 'updatedAt'
  | 'backgroundCheckType'
  | 'analyst'
  | 'opportunityType';
const columnDefinition: ColumnDefinition<
  FormattedAgFinanceRecord,
  ColumnId,
  IDefinitionContext,
  AgFinanceQueueCustomActions
>[] = [
  {
    id: 'name',
    header: i18next.t('name'),
    cell: ({ row, definitionContext }) => {
      const formattedRowName = text.toTitleCase(row.name);
      return (
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <Link
            to={`/br-growers/${row.id}`}
            onClick={definitionContext?.onDetailClick ? definitionContext.onDetailClick : undefined}
          >
            <RegularTitle style={{ fontWeight: 400, color: 'inherit' }}>{formattedRowName}</RegularTitle>
          </Link>
          {definitionContext?.showCopyButton ? <CopyToClipboardButton valueToCopy={formattedRowName} /> : null}
        </div>
      );
    },
    constraints: {
      min: { rem: 18 },
      max: { fr: 1 }
    }
  },
  {
    id: 'date',
    header: i18next.t('dateEntry'),
    cell: ({ row, SimpleCell }) => (
      <div style={{ whiteSpace: 'break-spaces' }}>
        <SimpleCell label={`${date.formatDate(row.date)}`} />
      </div>
    ),
    constraints: {
      min: { rem: 4 },
      max: { fr: 1 }
    }
  },
  {
    id: 'updatedAt',
    header: i18next.t('dateUpdated'),
    cell: ({ row, SimpleCell }) => (
      <div style={{ whiteSpace: 'break-spaces' }}>
        <SimpleCell label={`${date.formatDate(row.updatedAt)}`} />
      </div>
    ),
    constraints: {
      min: { rem: 4 },
      max: { fr: 1 }
    }
  },
  {
    id: 'stage',
    header: i18next.t('stage'),
    cell: ({ row, SimpleCell }) => <SimpleCell label={text.toTitleCase(row.stage)} />,
    constraints: {
      min: { rem: 5 },
      max: { fr: 1 }
    }
  },
  {
    id: 'state',
    header: i18next.t('state'),
    cell: ({ row, SimpleCell }) => <SimpleCell label={row.state} />,
    constraints: {
      min: { rem: 5 },
      max: { fr: 1 }
    }
  },
  {
    id: 'cpfOrCnpj',
    header: i18next.t('cpfNumber'),
    cell: ({ row, SubtitleCell }) => {
      // TODO: revisit after they implement tooltips
      const isValidCpfOrCnpj = brazil.validCpf(row.cpfOrCnpj);
      const unformattedCpfOrCnpj = brazil.cleanCpfCnpjValue(row.cpfOrCnpj);
      return (
        <>
          <div style={{ display: 'flex', alignItems: 'baseline' }}>
            <SubtitleCell label={row.cpfOrCnpj} sublabel={row.type} />
            <CopyToClipboardButton valueToCopy={unformattedCpfOrCnpj} />
          </div>
          {!isValidCpfOrCnpj && invalidCpfCnpjTooltip}
        </>
      );
    },
    constraints: {
      min: { rem: 9 },
      max: { fr: 1 }
    }
  },
  {
    id: 'campaign',
    header: i18next.t('campaign'),
    cell: ({ row, SubtitleCell }) => (
      <SubtitleCell
        label={row.campaign}
        sublabel={row.opportunities && row.opportunities?.length > 1 ? row.opportunities[0]?.collateralGroup : ''}
      />
    ),
    constraints: {
      min: { rem: 7 },
      max: { fr: 1 }
    }
  },
  {
    id: 'backgroundCheckType',
    header: text.firstLetterUppercase(i18next.t('backgroundCheckType')),
    cell: ({ row: { opportunities }, SimpleCell }) => {
      const opportunity = opportunities?.length ? [...opportunities].shift() : null;
      return <SimpleCell label={opportunity?.backgroundCheckType ?? '-'} />;
    },
    constraints: {
      min: { rem: 3 },
      max: { fr: 1 }
    }
  },
  {
    id: 'opportunityType',
    header: i18next.t('opportunityType'),
    cell: ({ row: { opportunities }, SimpleCell }) => {
      const opportunity = opportunities?.length ? [...opportunities].shift() : null;
      return <SimpleCell label={opportunity?.type ? i18next.t(`opportunityType-${opportunity.type}`) : '-'} />;
    },
    constraints: {
      min: { rem: 5 },
      max: { fr: 1 }
    }
  },
  {
    id: 'analyst',
    header: text.toTitleCase(i18next.t('creditAnalyst')),
    cell: ({ row, definitionContext, onTableAction }) => {
      const canEditSelection =
        definitionContext &&
        (definitionContext.isSuper || definitionContext.isCreditLead || definitionContext.isCredit);
      return (
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <EditableCreditAnalyst
            readonly={!canEditSelection}
            creditAnalystAssigned={row.creditAnalystAssigned}
            onChange={event =>
              onTableAction({
                type: TABLE_ACTIONS.UPDATE_CREDIT_ANALYST,
                payload: { userId: event.target.value, agFinancingId: row.id }
              })
            }
          />
        </div>
      );
    },
    constraints: {
      min: { rem: 5 },
      max: { fr: 1 }
    }
  }
];

const rowDefinition: RowDefinition<FormattedAgFinanceRecord, ColumnId, IDefinitionContext> = {
  rowId: ({ row }) => row.id,
  footer: ({
    row: { id, opportunities, phase2, prioritySince, hasActiveConversations, matriculaCount, kmlCount, collateralType },
    TagsFooter,
    definitionContext
  }) => {
    const tags: TagsFooterProps['tags'] = [];

    if (prioritySince) {
      tags.push({
        id: 'prioritySince',
        label: prioritySince.toString(),
        icon: faStar,
        iconColor: palettes.brand.red.base
      });
    }

    if (hasActiveConversations) {
      tags.push({
        id: `${id}-conversation`,
        label: i18next.t('conversations'),
        icon: faBell,
        iconColor: palettes.brand.yellow.base
      });
    }

    if (definitionContext && !definitionContext.isTerramagna) {
      if (opportunities?.length === 1 && opportunities[0]?.contractValue) {
        tags.push({
          id: 'amount',
          label: currency.inputMaskCurrency(opportunities[0]?.contractValue, {
            useComaForDecimals: true,
            currencySymbol: CURRENCY_SYMBOL.BR
          }),
          role: 'amount'
        });
      }

      if (
        collateralType &&
        collateralType !== CollateralType.NOT_FOUND &&
        collateralType !== CollateralType.NOT_APPLICABLE
      ) {
        tags.push({
          id: 'collateralType',
          label: i18next.t(collateralType),
          iconColor: palettes.brand.blue.base
        });
      }

      if (
        definitionContext &&
        definitionContext.activeTab !== TabIds.BACKGROUND_CHECK &&
        definitionContext.activeTab !== TabIds.DOCUMENT_REGISTRATION &&
        definitionContext.activeTab !== TabIds.DOCUMENT_REGISTRATION_EXTERNAL &&
        definitionContext.activeTab !== TabIds.INVESTMENT_FUND_ASSIGNMENT &&
        !!phase2
      ) {
        Object.entries(calculateStages(phase2))
          .filter(([stage]) => !!stage)
          .map(([stage, completed], index) =>
            tags.push({
              id: `${index}-${stage}`,
              label: stage,
              icon: completed ? faCheckCircle : faMinusCircle,
              iconColor: completed ? palettes.brand.green.base : palettes.brand.gray['700']
            })
          );
      }
    } else {
      if (!!matriculaCount) {
        tags.push({
          icon: faFileAlt,
          iconColor: palettes.brand.blue.base,
          id: 'matriculas',
          label: `${i18next.t('matriculas')} (${matriculaCount})`,
          role: 'amount'
        });
      }

      if (!!kmlCount) {
        tags.push({
          icon: faDrawPolygon,
          iconColor: palettes.coastal.accent.yellow,
          id: 'kml',
          label: `KML (${kmlCount})`,
          role: 'amount'
        });
      }
    }

    return <TagsFooter tags={tags} />;
  }
};

const getCurrentColumns = (isTerramagna: boolean, searching?: boolean, isInAnalysisTab?: boolean): ColumnId[] => {
  const currentColumns: ColumnId[] = ['name', 'date', 'updatedAt'];
  if (searching) {
    currentColumns.push('stage');
  }
  currentColumns.push('state', 'cpfOrCnpj');
  if (!isTerramagna) {
    currentColumns.push('campaign');
  }

  if (isInAnalysisTab) {
    currentColumns.push('backgroundCheckType');
  }

  currentColumns.push('analyst', 'opportunityType');

  return currentColumns;
};

export const AgFinancingQueue: React.FunctionComponent<AgFinancingQueueProps> = ({
  queue,
  pagination,
  onTableAction,
  activeTab,
  sort,
  searching,
  onDetailClick,
  activePill
}) => {
  /**
   * User Context
   */
  const userContext = useContext(UserContext);
  const isTerramagna = userContext.hasPermission(Permissions.FA_EXTERNAL_FIELD_MONITORING);
  const isCreditLead = userContext.hasPermission(Permissions.FA_CREDIT_LEAD);
  const isCredit = userContext.hasPermission(Permissions.FA_CREDIT);
  const isSuper = userContext.hasPermission(Permissions.FA_ADMIN);
  const isInAnalysisTab = PillIds.CREDIT_ANALYSIS_IN_PROGRESS === activePill;
  /**
   * Styles
   */
  const classes = useStyles();

  return queue && queue.length === 0 ? (
    <NoResultsFound />
  ) : (
    <div className={classes.tableStyles}>
      <Table
        label="AgFinance Queue"
        data={queue}
        columnDefinition={columnDefinition}
        currentColumns={getCurrentColumns(isTerramagna, searching, isInAnalysisTab)}
        rowDefinition={rowDefinition}
        paginationStyle="prev-next"
        pagination={pagination}
        onTableAction={onTableAction}
        sort={sort || undefined}
        sortable={sort ? (['state', 'campaign', 'date', 'updatedAt'] as ColumnId[]) : undefined}
        definitionContext={{
          showCopyButton: TabIds.BACKGROUND_CHECK === activeTab,
          onDetailClick,
          isTerramagna,
          displayStage: searching,
          activeTab,
          isCreditLead,
          isCredit,
          isSuper
        }}
      />
    </div>
  );
};
